import { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Spinner } from '@/components/ui/atoms/spinner';

/**
 * @ai-context Deep Link Redirect Handler
 *
 * Purpose: Redirects from /c/{slug}/... paths to the subdomain-based routing system.
 *
 * Example:
 * - /c/acme/feed -> acme.terrarium.app/feed
 * - /c/acme/events/123 -> acme.terrarium.app/events/123
 *
 * This enables deep links from the mobile app and email notifications
 * to work with the existing subdomain-based architecture.
 */
export function DeepLinkRedirect() {
  const { slug, '*': restPath } = useParams();
  const location = useLocation();

  useEffect(() => {
    if (!slug) {
      console.error('DeepLinkRedirect: No community slug provided');
      return;
    }

    // Build the target URL on the community subdomain
    const protocol = window.location.protocol;
    const port = window.location.port;
    const hostname = window.location.hostname;

    // Determine the base domain
    let baseDomain: string;

    // Development: local.terrakube.com or localhost
    if (
      hostname.includes('local.terrakube.com') ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1'
    ) {
      baseDomain = 'local.terrakube.com';
    } else if (hostname.includes('terrarium.app')) {
      // Production: terrarium.app
      baseDomain = 'terrarium.app';
    } else {
      // Fallback for other domains (staging, etc.)
      // Remove subdomain if present to get base domain
      const parts = hostname.split('.');
      baseDomain = parts.length > 2 ? parts.slice(1).join('.') : hostname;
    }

    // Build the redirect URL
    const path = restPath || '';
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const finalPath = normalizedPath === '/' ? '/feed' : normalizedPath;

    // Include port for development
    const portSuffix = port ? `:${port}` : '';

    const redirectUrl = `${protocol}//${slug}.${baseDomain}${portSuffix}${finalPath}${location.search}${location.hash}`;

    console.log('DeepLinkRedirect: Redirecting', {
      from: window.location.href,
      to: redirectUrl,
      slug,
      path: finalPath,
    });

    // Perform the redirect
    window.location.replace(redirectUrl);
  }, [slug, restPath, location.search, location.hash]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-brand-background">
      <Spinner size="lg" />
      <p className="mt-4 text-sm text-gray-600">Redirecting to community...</p>
    </div>
  );
}

export default DeepLinkRedirect;
